import { useEffect, useMemo, useRef } from 'react'

const DEFAULT_COLOR_1 = '#9bdfff'
const DEFAULT_COLOR_2 = '#82dfa7'

const vertexShader = `
  attribute vec2 aPosition;
  varying vec2 vUv;

  void main() {
    vUv = (aPosition + 1.0) * 0.5;
    gl_Position = vec4(aPosition, 0.0, 1.0);
  }
`

const fragmentShader = `
  precision highp float;

  uniform float uTime;
  uniform vec2 uResolution;
  uniform vec3 uColor1;
  uniform vec3 uColor2;
  varying vec2 vUv;

  vec3 permute(vec3 value) {
    return mod(((value * 34.0) + 1.0) * value, 289.0);
  }

  float simplexNoise(vec2 point) {
    const vec4 constants = vec4(
      0.211324865405187,
      0.366025403784439,
      -0.577350269189626,
      0.024390243902439
    );

    vec2 cell = floor(point + dot(point, constants.yy));
    vec2 localPoint = point - cell + dot(cell, constants.xx);
    vec2 offset = localPoint.x > localPoint.y ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 corners = localPoint.xyxy + constants.xxzz;
    corners.xy -= offset;
    cell = mod(cell, 289.0);

    vec3 permutation = permute(
      permute(cell.y + vec3(0.0, offset.y, 1.0))
      + cell.x
      + vec3(0.0, offset.x, 1.0)
    );
    vec3 falloff = max(
      0.5 - vec3(
        dot(localPoint, localPoint),
        dot(corners.xy, corners.xy),
        dot(corners.zw, corners.zw)
      ),
      0.0
    );
    falloff = falloff * falloff * falloff * falloff;

    vec3 gradient = 2.0 * fract(permutation * constants.www) - 1.0;
    vec3 height = abs(gradient) - 0.5;
    vec3 offsetGradient = floor(gradient + 0.5);
    vec3 adjustedGradient = gradient - offsetGradient;
    falloff *= 1.79284291400159
      - 0.85373472095314 * (adjustedGradient * adjustedGradient + height * height);

    vec3 contribution;
    contribution.x = adjustedGradient.x * localPoint.x + height.x * localPoint.y;
    contribution.yz = adjustedGradient.yz * corners.xz + height.yz * corners.yw;
    return 130.0 * dot(falloff, contribution);
  }

  float bayer4x4(vec2 point) {
    vec2 cell = mod(floor(point), 4.0);
    float x = cell.x;
    float y = cell.y;

    if (y < 1.0) {
      if (x < 1.0) return 0.0;
      if (x < 2.0) return 8.0;
      if (x < 3.0) return 2.0;
      return 10.0;
    }
    if (y < 2.0) {
      if (x < 1.0) return 12.0;
      if (x < 2.0) return 4.0;
      if (x < 3.0) return 14.0;
      return 6.0;
    }
    if (y < 3.0) {
      if (x < 1.0) return 3.0;
      if (x < 2.0) return 11.0;
      if (x < 3.0) return 1.0;
      return 9.0;
    }
    if (x < 1.0) return 15.0;
    if (x < 2.0) return 7.0;
    if (x < 3.0) return 13.0;
    return 5.0;
  }

  void main() {
    vec2 uv = vUv;
    float slowDrift = simplexNoise(uv * 1.35 + vec2(uTime * 0.16, -uTime * 0.10));
    float fineDrift = simplexNoise(uv * 3.1 - vec2(uTime * 0.11, uTime * 0.07));
    float diagonal = dot(uv, vec2(0.82, 0.54));
    float tide = sin((diagonal + slowDrift * 0.16 - uTime * 0.13) * 6.28318530718);
    float field = clamp(diagonal * 0.94 + slowDrift * 0.23 + fineDrift * 0.08 + tide * 0.12, 0.0, 1.0);
    float dither = (bayer4x4(gl_FragCoord.xy) / 16.0 - 0.5) * 0.045;
    float steppedField = floor(clamp(field + dither, 0.0, 1.0) * 7.0) / 7.0;
    vec3 color = mix(uColor1, uColor2, smoothstep(0.04, 0.96, steppedField));
    float contourPhase = fract((field - uTime * 0.12) * 6.0);
    float contourDistance = min(contourPhase, 1.0 - contourPhase);
    float contour = 1.0 - smoothstep(0.018, 0.052, contourDistance);
    color = mix(color, vec3(0.29, 0.36, 0.82), contour * 0.26);
    gl_FragColor = vec4(color, 1.0);
  }
`

function colorChannels(value, fallback) {
  const normalized = /^#[0-9a-f]{6}$/i.test(value) ? value : fallback
  return [1, 3, 5].map((index) => Number.parseInt(normalized.slice(index, index + 2), 16) / 255)
}

function compileShader(gl, type, source) {
  const shader = gl.createShader(type)
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS)) return shader
  gl.deleteShader(shader)
  return null
}

function createProgram(gl) {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexShader)
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentShader)
  if (!vertex || !fragment) return null

  const program = gl.createProgram()
  gl.attachShader(program, vertex)
  gl.attachShader(program, fragment)
  gl.linkProgram(program)
  gl.deleteShader(vertex)
  gl.deleteShader(fragment)
  if (gl.getProgramParameter(program, gl.LINK_STATUS)) return program
  gl.deleteProgram(program)
  return null
}

export default function HeroGeometric({
  children,
  className = '',
  color1 = DEFAULT_COLOR_1,
  color2 = DEFAULT_COLOR_2,
  speed = 0.34,
}) {
  const rootRef = useRef(null)
  const canvasRef = useRef(null)
  const colors = useMemo(() => ({
    first: colorChannels(color1, DEFAULT_COLOR_1),
    second: colorChannels(color2, DEFAULT_COLOR_2),
  }), [color1, color2])

  useEffect(() => {
    const root = rootRef.current
    const canvas = canvasRef.current
    const gl = canvas?.getContext('webgl', {
      alpha: false,
      antialias: false,
      powerPreference: 'high-performance',
    })
    if (!root || !canvas || !gl) return undefined

    const program = createProgram(gl)
    if (!program) return undefined

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    )
    gl.useProgram(program)
    const position = gl.getAttribLocation(program, 'aPosition')
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

    const timeUniform = gl.getUniformLocation(program, 'uTime')
    const resolutionUniform = gl.getUniformLocation(program, 'uResolution')
    gl.uniform3fv(gl.getUniformLocation(program, 'uColor1'), colors.first)
    gl.uniform3fv(gl.getUniformLocation(program, 'uColor2'), colors.second)

    let frame = 0
    let elapsed = 0
    let lastFrame = performance.now()
    let inViewport = true
    let documentVisible = document.visibilityState !== 'hidden'

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.25)
      const width = Math.max(1, Math.round(root.clientWidth * dpr))
      const height = Math.max(1, Math.round(root.clientHeight * dpr))
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
        gl.viewport(0, 0, width, height)
        gl.uniform2f(resolutionUniform, width, height)
      }
    }

    const wake = () => {
      if (frame || !inViewport || !documentVisible) return
      lastFrame = performance.now()
      frame = requestAnimationFrame(draw)
    }

    const draw = (now) => {
      frame = 0
      if (!inViewport || !documentVisible) return
      resize()
      elapsed += Math.min((now - lastFrame) / 1_000, 0.05) * Math.max(0, Math.min(speed, 3))
      lastFrame = now
      gl.uniform1f(timeUniform, elapsed)
      gl.drawArrays(gl.TRIANGLES, 0, 6)
      frame = requestAnimationFrame(draw)
    }

    const resizeObserver = new ResizeObserver(() => {
      resize()
      wake()
    })
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      inViewport = entry.isIntersecting
      if (inViewport) wake()
    }, { rootMargin: '160px 0px' })
    const onVisibilityChange = () => {
      documentVisible = document.visibilityState !== 'hidden'
      if (documentVisible) wake()
    }

    resizeObserver.observe(root)
    intersectionObserver.observe(root)
    document.addEventListener('visibilitychange', onVisibilityChange)
    resize()
    wake()

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      intersectionObserver.disconnect()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      gl.deleteBuffer(buffer)
      gl.deleteProgram(program)
    }
  }, [colors, speed])

  return (
    <section
      ref={rootRef}
      className={['settlementos-hero-geometric', className].filter(Boolean).join(' ')}
      style={{
        position: 'relative',
        minHeight: '100dvh',
        width: '100%',
        overflow: 'hidden',
        isolation: 'isolate',
        background: `linear-gradient(180deg, ${color1}, ${color2})`,
      }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      />
      <div style={{ position: 'relative', zIndex: 1, minHeight: '100dvh', width: '100%' }}>
        {children}
      </div>
    </section>
  )
}
